import { mapGetters } from 'vuex';
import { safeCreateElement } from './domQuery';

const SIGNATURE_MESSAGE_TYPES = new Set([
  'new',
  'reply',
  'replyAll',
  'forward',
]);

const LEGACY_SIGNATURE_ENABLE_KEYS = [
  'signature_new_email_enabled',
  'signature_reply_forward_enabled',
  'channel_email_signature_enabled',
];

const QUOTED_CONTENT_SELECTOR = [
  '[data-gcp-quoted-toggle]',
  '[data-gcp-quote-header]',
  '[data-gcp-collapsed-quote]',
  '[data-gcp-forward-start]',
  'blockquote',
  '.gmail_quote',
  '.quoted',
].join(', ');

const normalizeSignatureText = (signature) =>
  String(signature || '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

export const hasMessageSignature = (signature) =>
  normalizeSignatureText(signature).length > 0 ||
  /<img\b/i.test(signature || '');

/**
 * There is a single personal signature switch. Existing users are migrated
 * conservatively: the signature is enabled only if one of the legacy controls
 * had explicitly enabled it, never merely because saved content exists.
 */
export const isSignatureEnabled = (user) => {
  const settings = user?.ui_settings || {};
  const configuredValue = settings.signature_enabled;
  if (typeof configuredValue === 'boolean') return configuredValue;

  return LEGACY_SIGNATURE_ENABLE_KEYS.some((key) => settings[key] === true);
};

/**
 * La firma (testo + interruttore) è per account: si legge dalla voce
 * dell'account corrente in user.accounts. Se la voce non li porta (risposta
 * di un server precedente) valgono i vecchi campi dell'utente. Speculare di
 * MessageSignatureSettings lato server.
 */
export const accountSignatureSettings = (user, accountId) => {
  const account = (user?.accounts || []).find(({ id }) => id === accountId);
  return {
    signature:
      typeof account?.message_signature === 'string'
        ? account.message_signature
        : user?.message_signature || '',
    enabled:
      typeof account?.signature_enabled === 'boolean'
        ? account.signature_enabled
        : isSignatureEnabled(user),
  };
};

export const shouldApplySignature = (
  user,
  messageType,
  isEmailChannel = true,
  signatureOverride = null
) =>
  Boolean(
    isEmailChannel &&
    SIGNATURE_MESSAGE_TYPES.has(messageType) &&
    (signatureOverride ?? isSignatureEnabled(user))
  );

export const shouldInsertBeforeQuoted = (_user, messageType) =>
  ['reply', 'replyAll', 'forward'].includes(messageType);

const buildRichSignatureHtml = (signature) =>
  `<div class="email-signature" data-signature="true"><div class="signature-content">${signature}</div></div>`;

const findQuotedContentIndex = (content) => {
  const quotedPatterns = [
    /<[^>]+data-gcp-(?:quoted-toggle|quote-header|collapsed-quote|forward-start)[^>]*>/i,
    /<blockquote\b[^>]*>/i,
    /<div[^>]*class=["'][^"']*(?:gmail_quote|quoted)[^"']*["'][^>]*>/i,
    /---------- Messaggio originale ----------/i,
    /---------- Conversazione inoltrata ----------/i,
    /(?:^|\n)(?:From|Da):/i,
    /^>/m,
  ];

  return quotedPatterns.reduce((position, pattern) => {
    const match = content.match(pattern);
    if (!match || (position !== -1 && match.index >= position)) return position;
    return match.index;
  }, -1);
};

const fallbackRemoveRichSignature = (content) => {
  const quoteIndex = findQuotedContentIndex(content);
  const beforeQuote =
    quoteIndex === -1 ? content : content.slice(0, quoteIndex);
  const afterQuote = quoteIndex === -1 ? '' : content.slice(quoteIndex);

  // Browser flows use the DOM branch below. This fallback keeps server-side
  // tests and non-DOM rendering safe for the canonical wrapper we generate.
  const withoutSignature = beforeQuote.replace(
    /<div\b(?=[^>]*\bdata-signature=["']true["'])[^>]*>\s*(?:<div[^>]*class=["'][^"']*signature-separator[^"']*["'][^>]*>[\s\S]*?<\/div>\s*)?<div[^>]*class=["'][^"']*signature-content[^"']*["'][^>]*>[\s\S]*?<\/div>\s*<\/div>/gi,
    ''
  );

  return `${withoutSignature}${afterQuote}`;
};

const transformRichContent = (content, signature = '') => {
  const container = safeCreateElement('div');
  if (!container) {
    const withoutSignature = fallbackRemoveRichSignature(content);
    if (!hasMessageSignature(signature)) return withoutSignature;

    const quoteIndex = findQuotedContentIndex(withoutSignature);
    const signatureHtml = buildRichSignatureHtml(signature);
    return quoteIndex === -1
      ? `${withoutSignature}${signatureHtml}`
      : `${withoutSignature.slice(0, quoteIndex)}${signatureHtml}${withoutSignature.slice(quoteIndex)}`;
  }

  container.innerHTML = content;
  container.querySelectorAll('[data-signature="true"]').forEach((node) => {
    const quotedAncestor = node.closest?.(QUOTED_CONTENT_SELECTOR);
    if (!quotedAncestor) node.remove();
  });
  const quoteStart = container.querySelector(QUOTED_CONTENT_SELECTOR);

  if (!hasMessageSignature(signature)) return container.innerHTML;

  const signatureNode = safeCreateElement('div');
  const signatureContent = safeCreateElement('div');
  if (!signatureNode || !signatureContent) return container.innerHTML;

  signatureNode.className = 'email-signature';
  signatureNode.setAttribute('data-signature', 'true');
  signatureContent.className = 'signature-content';
  signatureContent.innerHTML = signature;
  signatureNode.appendChild(signatureContent);

  if (quoteStart?.parentNode) {
    quoteStart.parentNode.insertBefore(signatureNode, quoteStart);
  } else {
    container.appendChild(signatureNode);
  }

  return container.innerHTML;
};

const removePlainSignature = (content, signature) => {
  if (!hasMessageSignature(signature)) return content;

  const quoteIndex = findQuotedContentIndex(content);
  const beforeQuote =
    quoteIndex === -1 ? content : content.slice(0, quoteIndex);
  const afterQuote = quoteIndex === -1 ? '' : content.slice(quoteIndex);
  const normalizedSignature = String(signature).trim();
  const signatureIndex = beforeQuote.trimEnd().lastIndexOf(normalizedSignature);

  if (signatureIndex === -1) return content;

  const trailingContent = beforeQuote
    .trimEnd()
    .slice(signatureIndex + normalizedSignature.length)
    .trim();
  if (trailingContent) return content;

  const cleanedBefore = beforeQuote
    .trimEnd()
    .slice(0, signatureIndex)
    .replace(/(?:\r?\n)?--\s*$/g, '')
    .trimEnd();

  return quoteIndex === -1
    ? cleanedBefore
    : `${cleanedBefore}\n\n${afterQuote.replace(/^\s+/, '')}`;
};

export const removeSignatureFromContent = (
  content,
  signature,
  isRichEditor = true
) => {
  const normalizedContent = String(content || '');
  return isRichEditor
    ? transformRichContent(normalizedContent)
    : removePlainSignature(normalizedContent, signature);
};

const insertPlainSignature = (content, signature, beforeQuoted) => {
  const withoutSignature = removePlainSignature(content, signature);
  const signatureText = String(signature).trim();
  const quoteIndex = beforeQuoted
    ? findQuotedContentIndex(withoutSignature)
    : -1;

  if (quoteIndex === -1) {
    return `${withoutSignature.trimEnd()}\n\n${signatureText}`.trimStart();
  }

  const beforeQuote = withoutSignature.slice(0, quoteIndex).trimEnd();
  const afterQuote = withoutSignature.slice(quoteIndex).replace(/^\s+/, '');
  return `${beforeQuote}\n\n${signatureText}\n\n${afterQuote}`.trimStart();
};

/**
 * Enforces the complete policy, rather than only adding a signature. This is
 * deliberately idempotent so restoring drafts and the final send guard cannot
 * create duplicates. When the effective switch is off, only the active signature
 * (never signatures inside quoted history) is removed.
 */
export const applySignatureToContent = (
  content,
  signature,
  user,
  messageType,
  isRichEditor = true,
  signatureOverride = null
) => {
  const normalizedContent = String(content || '');
  const shouldApply =
    shouldApplySignature(user, messageType, true, signatureOverride) &&
    hasMessageSignature(signature);

  if (isRichEditor) {
    return transformRichContent(
      normalizedContent,
      shouldApply ? signature : ''
    );
  }

  if (!shouldApply) {
    return removePlainSignature(normalizedContent, signature);
  }

  return insertPlainSignature(
    normalizedContent,
    signature,
    shouldInsertBeforeQuoted(user, messageType)
  );
};

// Scelta del compositore se c'è, altrimenti l'interruttore dell'account
// corrente; senza nessuno dei due vale la vecchia regola sull'utente.
const signatureChoice = (component) =>
  component.messageSignatureOverride ?? component.signatureEnabled;

export const signatureSettingsMixin = {
  // A composer choice is local; the profile setting remains the default.
  data: () => ({ messageSignatureOverride: null }),
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      signatureEnabled: 'getSignatureEnabled',
    }),
  },

  methods: {
    toggleMessageSignature() {
      if (!this.isAnEmailChannel || this.isPrivate || this.isOnPrivateNote) {
        return;
      }

      const shouldAdd = !this.sendWithSignature;
      if (shouldAdd && !hasMessageSignature(this.messageSignature)) return;

      this.messageSignatureOverride = shouldAdd;
      const editor = this.$refs.emailEditor;
      if (this.showRichContentEditor && editor?.handleToggleSignature) {
        editor.handleToggleSignature({
          shouldAdd,
          signature: this.messageSignature,
        });
      } else {
        this.message = this.applySignatureBasedOnSettings(
          this.message,
          'reply'
        );
      }
    },

    shouldApplySignatureForMessage(messageType) {
      return shouldApplySignature(
        this.currentUser,
        messageType,
        this.isAnEmailChannel,
        signatureChoice(this)
      );
    },

    shouldInsertSignatureBeforeQuoted(messageType) {
      return shouldInsertBeforeQuoted(this.currentUser, messageType);
    },

    applySignatureBasedOnSettings(content, messageType) {
      if (!this.isAnEmailChannel) return content;

      return applySignatureToContent(
        content,
        this.signatureToApply ?? this.messageSignature,
        this.currentUser,
        messageType,
        this.showRichContentEditor,
        signatureChoice(this)
      );
    },
  },
};

export default {
  accountSignatureSettings,
  applySignatureToContent,
  hasMessageSignature,
  isSignatureEnabled,
  removeSignatureFromContent,
  shouldApplySignature,
  shouldInsertBeforeQuoted,
  signatureSettingsMixin,
};
